import React, { useEffect, useState } from 'react'
import { isEmpty } from 'lodash'
import { useHistory } from 'react-router-dom'
import { useKeyPress, utils, produce, useForm } from 'passiv-libs'

import { SSO, CPI, LPA, PUSH } from 'passiv-api'
import { setUrls } from './utils'

import {
  setAccount,
  clearUserData,
  toggleAutoLogin,
  setAuthDetails,
  // setUserEmailAddress,
  setHomeHubId,
  setHitachiSEIProject
} from './utils'

import Login from './login'

let hasSubmitted = false

const LoginContainer = () => {
  const history = useHistory()

  const [state, setState] = useState({
    loading: false,
    error: '',
    autoLogin: utils.settings.getAutoLogin()
  })

  const { register, getValues, watch, errors } = useForm({ mode: 'onChange' })

  const refs = {
    username: register({ required: true }),
    password: register({ required: true }),
    server: register()
  }

  const lastKey = useKeyPress()

  // login via hitting enter - PoC
  useEffect(() => {
    const { username, password, server } = getValues()
    const fieldComplete = server !== '' && username !== '' && password !== ''
    if (!hasSubmitted && lastKey === 'Enter' && fieldComplete && password !== 'trialsettings') {
      hasSubmitted = true
      login()
    }
  })

  useEffect(() => {
    resetState()
  }, [])

  const trialSettings =
    (process.env.NODE_ENV === 'development' || utils.cordova.isCordova()) &&
    watch('password') === 'trialsettings'

  if (trialSettings) {
    const server = watch('server')
    setUrls(server)
  }

  const resetState = () => {
    clearUserData()

    utils.settings.setSetupDismissed(false)

    if (utils.cordova.isCordova() && PUSH.isRegistered()) {
      PUSH.setAppBadge(0)
      PUSH.unregister()
    }
  }

  const login = async () => {
    const { username, password, server } = getValues()

    setState(
      produce(draft => {
        draft.loading = true
      })
    )

    try {
      const { token } = await SSO.login({
        username: username.trim(),
        password: password.trim(),
        server
      })

      setAuthDetails({ token, server, username, autoLogin: state.autoLogin })

      const homeHubID = await CPI.homeHubID()

      setHomeHubId(homeHubID)

      const CPI_LPA = await Promise.all([
        await CPI.GET({ capability: '' }),
        await LPA.services()
      ])

      setAccount(CPI_LPA[0])

      setHitachiSEIProject(CPI_LPA[1])

      // DEV-31287 Hubs need to be at least V5001.38 to use River UI App
      // If not we need to shop them an error modal
      // TODO only do this if zwave and has heating or immersion hot water
      const hasHeatingOrImmersionHW = CPI_LPA[0]._embedded.capabilities.find(({id}) => id === 'heating' || id === 'immersionhotwater')
      if (CPI_LPA[0].hubType === 'zwave' && hasHeatingOrImmersionHW && parseFloat(CPI_LPA[0].hubVersion) < 5001.38) {

        setState(
          produce(draft => {
            draft.loading = false
          })
        )
        utils.general.triggerErrorModal({ 
          type: 'OUTDATED_HUB',
          title: 'Hub software update required',
          message: 'Your hub is running software too old to support this version of the app. Please contact Passiv at support@passivuk.com',
          buttons: [
            {
              text: 'Ok',
              onClick: async () => {
                const { email, subject, body } = await utils.general.getHubSoftwareUpdateEmailFields()
                utils.cordova.openSystemLink(`mailto:${email}?subject=${subject}&body=${body}`)
              }
            },
            {
              text: 'Cancel',
              onClick: () => {
                resetState()
              }
            }
          ]
         })
      } else {

        if (utils.cordova.isCordova()) {
          window.config.__REDIRECTTOLOGIN__ = false
        }
  
        await SSO.cacheCreds()
  
        history.push('/')

      }

    } catch (err) {
      setState(
        produce(draft => {
          draft.loading = false
          if (err) draft.error = err.type
        })
      )
    }
  }

  const _toggleAutoLogin = () => {
    const bool = utils.settings.getAutoLogin()

    toggleAutoLogin(!bool)
    setState(
      produce(draft => {
        draft.autoLogin = !bool
      })
    )
  }

  const forgotPassword = () => {
    if (utils.cordova.isCordova()) {
      // DEV-26884: Google recaptcha and LPA are expecting a domain to be specified by the referer header.
      // The mobile app has no concept of a domain, so no referer header is sent
      // Therefore the mobile app should point to portal      
      utils.cordova.openLink(`${utils.settings.getServerUrl()}/passivlivingconsumer/#/forgot`, '_blank', 'location=no')
    } else {
      history.push('/forgot')
    }
  }

  return (
    <Login
      {...state}
      refs={refs}
      trialSettings={trialSettings}
      disabled={!isEmpty(errors)}
      serverUrl={utils.settings.getServerUrl()}
      username={utils.settings.getUsername()}
      onClickQuery={() => utils.cordova.openLink(config.urls['trouble-logging-on'])}
      onClickForgot={forgotPassword}
      onClickPrivacy={() => utils.cordova.openLink(config.urls['privacy-policy'])}
      onClickReset={() => history.push('/forgot')}
      onToggleAutoLogin={_toggleAutoLogin}
      onLogin={login}
      onClearError={() => {
        setState(
          produce(draft => {
            draft.error = ''
          })
        )
      }}
      onSelectTrialSettings={e => {
        refs.server.current.value = e.target.value
        setUrls(e.target.value)
      }}
    />
  )
}

export default LoginContainer
